<template>
  <div class="duplicate-controls px-6 py-4">
    <div>
      <system-avatar v-if="isSystemUser" small class="mr-1" />
      <researcher-avatar
        v-else-if="resolvedBy"
        :researcher="resolvedBy"
        small
        class="mr-1"
      />
      <span v-if="prefixWithUserName" class="font-weight-bold">
        {{ resolvedByName }}
      </span>
      <span class="font-weight-light">{{ prefixText }}</span>
      <span class="font-weight-bold mr-1">
        {{ actionText }}
      </span>
      <duplicate-event-history-popup :reference="reference" />
    </div>
    <div class="buttons">
      {{ buttonsLabel }}
      <action-button
        v-for="button in buttons"
        :key="button.onClickEvent"
        height="30"
        :color="button.color"
        @click="$emit(button.onClickEvent)"
      >
        <v-icon left>{{ button.icon }}</v-icon>
        {{ button.text }}
      </action-button>
    </div>
  </div>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import ResearcherAvatar from '@/components/Common/ResearcherAvatar';
import SystemAvatar from '@/components/Common/SystemAvatar';
import DuplicateEventHistoryPopup from './DuplicateEventHistoryPopup';
import { Reference } from '@/models';
import { DuplicateState } from '@/enums';

const CONFIRM_DUPLICATE_BUTTON = {
  text: 'Yes',
  color: 'primary',
  icon: 'check',
  onClickEvent: 'mark-as-duplicate',
};

const CONFIRM_NOT_DUPLICATE_BUTTON = {
  text: 'No',
  icon: 'close',
  onClickEvent: 'mark-as-not-duplicate',
};

const MARK_AS_DUPLICATE_BUTTON = {
  text: 'Mark as Duplicate',
  color: 'primary',
  icon: 'undo',
  onClickEvent: 'mark-as-duplicate',
};

const MARK_NOT_DUPLICATE_BUTTON = {
  text: 'Not a Duplicate',
  icon: 'undo',
  onClickEvent: 'mark-as-not-duplicate',
};

const STATE_TO_PROPS = {
  [DuplicateState.USER_DUPLICATE]: {
    prefixWithUserName: true,
    prefixText: 'marked this as a',
    actionText: 'Duplicate',
    buttons: [MARK_NOT_DUPLICATE_BUTTON],
  },
  [DuplicateState.AUTO_DUPLICATE]: {
    prefixWithUserName: false,
    prefixText: 'Automatically resolved as a',
    actionText: 'Duplicate',
    buttons: [MARK_NOT_DUPLICATE_BUTTON],
  },
  [DuplicateState.POTENTIAL_DUPLICATE]: {
    buttonsLabel: 'Is this a duplicate?',
    prefixWithUserName: false,
    prefixText: 'Flagged for',
    actionText: 'Review',
    buttons: [CONFIRM_DUPLICATE_BUTTON, CONFIRM_NOT_DUPLICATE_BUTTON],
  },
  [DuplicateState.NOT_DUPLICATE]: {
    prefixWithUserName: true,
    prefixText: 'marked this as',
    actionText: 'Not a Duplicate',
    buttons: [MARK_AS_DUPLICATE_BUTTON],
  },
};

export default {
  name: 'DuplicateControls',
  components: {
    ActionButton,
    ResearcherAvatar,
    SystemAvatar,
    DuplicateEventHistoryPopup,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
  },
  emits: ['mark-as-duplicate', 'mark-as-not-duplicate'],
  computed: {
    prefixWithUserName() {
      return this.stateProps.prefixWithUserName;
    },
    prefixText() {
      return this.stateProps.prefixText;
    },
    actionText() {
      return this.stateProps.actionText;
    },
    buttons() {
      return this.stateProps.buttons;
    },
    buttonsLabel() {
      return this.stateProps.buttonsLabel;
    },
    stateProps() {
      const stateProps = STATE_TO_PROPS[this.duplicateState];

      // Use "Marked as a" when we don't know the resolver's name.
      if (stateProps.prefixWithUserName && !this.resolvedBy) {
        stateProps.prefixText = 'Marked as a';
      }

      return stateProps;
    },
    duplicateState() {
      return this.reference.duplicateState();
    },
    isSystemUser() {
      return [
        DuplicateState.AUTO_DUPLICATE,
        DuplicateState.POTENTIAL_DUPLICATE,
      ].includes(this.duplicateState);
    },
    resolvedBy() {
      return this.reference.duplicateStateResolvedBy();
    },
    resolvedByName() {
      return this.resolvedBy?.name;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.duplicate-controls {
  background: map-get($grey, 'lighten-4');

  display: flex;
  justify-content: space-between;
  align-items: center;

  font-size: 14px;

  .buttons {
    display: grid;
    align-items: center;
    grid-auto-flow: column;
    gap: 12px;
  }
}
</style>
